import React, { useEffect } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: #000;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 50;
  padding: 0 5%;
  pointer-events: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const SolutionsSection = styled.div`
  margin-top: 100px; /* Space for the fixed navbar */
  padding: 2rem 0;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const SectionTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.79);
  text-align: center;
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  max-width: 900px;
  margin: 0 auto 3rem auto;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);

  .tagline {
    display: block;
    font-size: 1.4rem;
    margin-bottom: 1.5rem;
    color: rgba(255, 255, 255, 1);
    font-weight: 500;
  }

  .description {
    display: block;
    max-width: 700px;
    margin: 0 auto;
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.8;
  }
`;

const SolutionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 1800px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SolutionCard = styled.div`
  background: rgba(30, 30, 30, 0.7);
  border-radius: 10px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const CustomSolutionCard = styled(SolutionCard)`
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.7), rgba(50, 50, 50, 0.7));
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3),
                0 0 20px rgba(255, 255, 255, 0.1);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 1rem;
  color: white;
`;

const CardDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;
`;

const CardFeatures = styled.ul`
  margin-left: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardFeature = styled.li`
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.5rem;
`;

const LearnMoreButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 30px;
  background: ${props => props.disabled 
    ? 'linear-gradient(45deg, rgb(150, 150, 150), rgb(100, 100, 100))' 
    : 'linear-gradient(45deg, rgb(255, 255, 255), rgb(236, 236, 236))'};
  color: ${props => props.disabled ? '#444' : 'black'};
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: transform 0.2s, box-shadow 0.2s;
  font-weight: 500;
  margin-top: auto;
  align-self: flex-start;

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 20px rgba(0, 0, 0, 0.9)'};
  }
`;

function Solutions() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    window.open('https://www.linkedin.com/company/geospatios', '_blank');
  };

  const solutionsList = [
    {
      title: "Maritime Operations",
      description: "Transform vessel routing and port operations with real-time intelligence. Deploy our system or integrate our Platform.",
      features: [
        "Dynamic route optimization with weather & traffic",
        "Port congestion prediction",
        "Fuel consumption analytics",
        "Environmental compliance tools"
      ]
    },
    
    {
      title: "Airspace Operations",
      description: "Power your flight operations with advanced geospatial solutions. Available as complete system or integration framework.",
      features: [
        "Intelligent dispatch optimization",
        "Seamless EFB integration",
        "Real-time weather & NOTAM processing",
        "Performance & emissions analytics"
      ]
    },
    {
      title: "Air Traffic Management",
      description: "Optimize airspace efficiency with predictive analytics. Use our system or integrate our processing pipeline.",
      features: [
        "Real-time capacity optimization",
        "Predictive conflict resolution",
        "Dynamic weather integration",
        "Environmental impact analysis"
      ]
    },
    {
      title: "Custom Integration",
      description: "Enhance your systems with our Geo OS Platform and let us help you build your ideal solution.",
      features: [
        "Flexible API & SDK access",
        "Custom pipeline integration",
        "Advanced analytics engine",
        "Full implementation support"
      ],
      isCustom: true
    }
  ];

  return (
    <PageContainer>
      <Navbar />
      <ContentContainer>
        <SolutionsSection>
          <SectionTitle>Our Solutions</SectionTitle>
          <IntroText>
            <span className="tagline">Comprehensive solutions for autonomous operations</span>
            <span className="description">
              Our platform provides end-to-end solutions for managing and optimizing autonomous operations across all domains.
            </span>
          </IntroText>
          
          <SolutionsGrid>
            {solutionsList.map((solution, index) => (
              solution.isCustom ? (
                <CustomSolutionCard key={index}>
                  <CardTitle>{solution.title}</CardTitle>
                  <CardDescription>{solution.description}</CardDescription>
                  <CardFeatures>
                    {solution.features.map((feature, featureIndex) => (
                      <CardFeature key={featureIndex}>{feature}</CardFeature>
                    ))}
                  </CardFeatures>
                  <LearnMoreButton onClick={handleContactClick}>
                    Contact Us
                  </LearnMoreButton>
                </CustomSolutionCard>
              ) : (
                <SolutionCard key={index}>
                  <CardTitle>{solution.title}</CardTitle>
                  <CardDescription>{solution.description}</CardDescription>
                  <CardFeatures>
                    {solution.features.map((feature, featureIndex) => (
                      <CardFeature key={featureIndex}>{feature}</CardFeature>
                    ))}
                  </CardFeatures>
                  <LearnMoreButton 
                    disabled={solution.title === "Maritime Operations" || solution.title === "Air Traffic Management"}
                    onClick={!solution.title === "Maritime Operations" && !solution.title === "Air Traffic Management" ? handleContactClick : undefined}
                  >
                    {solution.title === "Maritime Operations" || solution.title === "Air Traffic Management" 
                      ? "Coming Soon" 
                      : "Contact Us"}
                  </LearnMoreButton>
                </SolutionCard>
              )
            ))}
          </SolutionsGrid>
        </SolutionsSection>
        <Footer />
      </ContentContainer>
    </PageContainer>
  );
}

export default Solutions; 